import { useRef, useState, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent } from 'react';

import { IconButton, JsonField, NumberField, SelectField, TextField } from '../ui/controls.js';
import {
  ENV_CURVES,
  LFO_SHAPES,
  PARAM_KINDS,
  PARAM_KIND_LABELS,
  adsrToEnv,
  convertParam,
  envDuration,
  envRange,
  evalEnv,
  exprVariables,
  insertEnvPoint,
  isNum,
  isObj,
  lfoValue,
  midiToNote,
  moveEnvPoint,
  mtof,
  noteToMidi,
  paramKind,
  pointCurve,
  r4,
  removeEnvPoint,
  setEnvCurve,
  tryEvalExpr,
  type EnvCurve,
  type EnvPoint,
  type LfoShape,
  type LfoSpec,
  type ParamKind,
} from './param-values.js';
import './sound-editors.css';

const W = 280;
const H = 96;
const PAD = 8;

/** `doc.variables` as a number map (non-numbers dropped). */
export function docVariables(doc: unknown): Record<string, number> {
  const vars = isObj(doc) && isObj(doc.variables) ? doc.variables : {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(vars)) if (isNum(v)) out[k] = v;
  return out;
}

interface Drag {
  index: number;
  points: EnvPoint[];
  range: [number, number];
  span: number;
  pointer: number;
}

/**
 * Draggable envelope editor for `[[time, value, curve?], ...]`: drag a point,
 * double-click to add, Delete removes the selected point; the selected point's
 * time/value/curve can be typed. The curve on a point shapes the segment
 * ending at it.
 */
export function EnvelopeEditor({
  points,
  onChange,
  min,
  max,
  duration,
  step = 0.05,
}: {
  points: EnvPoint[];
  onChange: (points: EnvPoint[]) => void;
  min?: number;
  max?: number;
  duration?: number;
  step?: number;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const drag = useRef<Drag | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [, redraw] = useState(0);

  let [lo, hi] = envRange(points, min, max);
  let span = Math.max(duration ?? 0, envDuration(points) * 1.1, 0.05);
  if (drag.current) {
    [lo, hi] = drag.current.range;
    span = drag.current.span;
  }
  const toX = (t: number) => PAD + (t / span) * (W - 2 * PAD);
  const toY = (v: number) => PAD + (1 - (v - lo) / (hi - lo)) * (H - 2 * PAD);
  const fromPointer = (e: { clientX: number; clientY: number }): [number, number] => {
    const rect = svg.current!.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const y = ((e.clientY - rect.top) / rect.height) * H;
    const t = Math.max(0, ((x - PAD) / (W - 2 * PAD)) * span);
    const f = Math.min(1, Math.max(0, (y - PAD) / (H - 2 * PAD)));
    return [r4(t), r4(lo + (1 - f) * (hi - lo))];
  };

  const selected = sel !== null && points[sel] ? sel : null;
  const selPoint = selected !== null ? points[selected] : undefined;

  function startDrag(e: ReactPointerEvent<SVGCircleElement>, index: number) {
    e.stopPropagation();
    e.preventDefault();
    svg.current?.focus();
    svg.current?.setPointerCapture(e.pointerId);
    setSel(index);
    drag.current = { index, points, range: [lo, hi], span, pointer: e.pointerId };
  }
  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    const [t, v] = fromPointer(e);
    d.points = moveEnvPoint(d.points, d.index, t, v);
    onChange(d.points);
  }
  function endDrag(e: ReactPointerEvent<SVGSVGElement>) {
    if (drag.current?.pointer !== e.pointerId) return;
    drag.current = null;
    redraw((n) => n + 1);
  }
  function onDoubleClick(e: ReactMouseEvent<SVGSVGElement>) {
    const [t, v] = fromPointer(e);
    const res = insertEnvPoint(points, t, v);
    onChange(res.points);
    setSel(res.index);
  }
  function removeSelected() {
    if (selected === null) return;
    onChange(removeEnvPoint(points, selected));
    setSel(null);
  }

  const samples: string[] = [];
  const N = 140;
  for (let i = 0; i <= N; i++) {
    const t = (i / N) * span;
    samples.push(`${r4(toX(t))},${r4(toY(evalEnv(points, t)))}`);
  }
  const zeroY = lo < 0 && hi > 0 ? toY(0) : null;

  return (
    <div className="snd-env">
      <svg
        ref={svg}
        className="snd-graph"
        viewBox={`0 0 ${W} ${H}`}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={onDoubleClick}
        onPointerDown={() => setSel(null)}
        onKeyDown={(e) => {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            removeSelected();
          }
        }}
        role="application"
        aria-label="Envelope editor: drag points, double-click to add, Delete to remove"
      >
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} className="snd-graph-grid" x1={PAD + f * (W - 2 * PAD)} x2={PAD + f * (W - 2 * PAD)} y1={PAD} y2={H - PAD} />
        ))}
        {zeroY !== null && <line className="snd-graph-zero" x1={PAD} x2={W - PAD} y1={zeroY} y2={zeroY} />}
        <polyline className="snd-graph-line" points={samples.join(' ')} />
        {points.map((p, i) => (
          <circle
            key={i}
            className={`snd-graph-key${selected === i ? ' is-selected' : ''}`}
            cx={toX(p[0])}
            cy={toY(p[1])}
            r={selected === i ? 4.5 : 3.5}
            onPointerDown={(e) => startDrag(e, i)}
          >
            <title>{`${r4(p[0])} s → ${r4(p[1])}${p.length === 3 ? ` (${p[2]})` : ''}`}</title>
          </circle>
        ))}
        <text className="snd-graph-label" x={PAD + 2} y={PAD + 8}>
          {r4(hi)}
        </text>
        <text className="snd-graph-label" x={PAD + 2} y={H - PAD - 2}>
          {r4(lo)}
        </text>
        <text className="snd-graph-label" x={W - PAD - 2} y={H - PAD - 2} textAnchor="end">
          {`${r4(span)} s`}
        </text>
      </svg>
      <div className="snd-row">
        {selected !== null && selPoint ? (
          <>
            <NumberField
              label="T"
              title="Point time (s)"
              value={selPoint[0]}
              step={0.01}
              min={0}
              onChange={(t) => onChange(moveEnvPoint(points, selected, t, selPoint[1]))}
            />
            <NumberField
              label="V"
              title="Point value"
              value={selPoint[1]}
              step={step}
              onChange={(v) => onChange(moveEnvPoint(points, selected, selPoint[0], v))}
            />
            <SelectField
              value={pointCurve(selPoint)}
              options={ENV_CURVES}
              onChange={(c) => onChange(setEnvCurve(points, selected, c as EnvCurve))}
            />
            <IconButton icon="trash" title="Delete point" size={13} disabled={points.length <= 2} onClick={removeSelected} />
          </>
        ) : (
          <span className="snd-hint">double-click adds a point · curve shapes the segment into a point</span>
        )}
      </div>
    </div>
  );
}

function Sparkline({ fn, span, lo, hi }: { fn: (t: number) => number; span: number; lo: number; hi: number }) {
  const N = 140;
  const pts: string[] = [];
  const range = hi - lo || 1;
  for (let i = 0; i <= N; i++) {
    const t = (i / N) * span;
    const y = PAD + (1 - (fn(t) - lo) / range) * (H - 2 * PAD);
    pts.push(`${r4(PAD + (i / N) * (W - 2 * PAD))},${r4(y)}`);
  }
  return (
    <svg className="snd-graph is-static" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline className="snd-graph-line" points={pts.join(' ')} />
      <text className="snd-graph-label" x={PAD + 2} y={PAD + 8}>
        {r4(hi)}
      </text>
      <text className="snd-graph-label" x={PAD + 2} y={H - PAD - 2}>
        {r4(lo)}
      </text>
      <text className="snd-graph-label" x={W - PAD - 2} y={H - PAD - 2} textAnchor="end">
        {`${r4(span)} s`}
      </text>
    </svg>
  );
}

export interface ParamFieldProps {
  value: unknown;
  onChange: (value: unknown) => void;
  step?: number;
  min?: number;
  max?: number;
  /** Pitch field: also offers note names and `{ hz }`. */
  pitch?: boolean;
  /** Forms offered in the mode picker (default: all). */
  kinds?: readonly ParamKind[];
  /** `doc.variables` for expressions (see {@link docVariables}). */
  vars?: Record<string, number>;
  /** Default envelope length in seconds. */
  duration?: number;
  /** Placeholder value when the field is absent (shown, not written). */
  placeholder?: number;
  /** Nesting depth (sum/product operands); limits recursion. */
  depth?: number;
}

/**
 * Edits any time-varying value of a sound document: a number, an envelope,
 * ADSR, LFO, random range, a variable expression, a note name / Hz (pitch
 * fields) or a sum/product of others. A mode picker converts between forms
 * keeping the value's representative number.
 */
export function ParamField({
  value,
  onChange,
  step = 0.1,
  min,
  max,
  pitch = false,
  kinds,
  vars = {},
  duration,
  placeholder,
  depth = 0,
}: ParamFieldProps) {
  const kind = paramKind(value);
  const offered = (kinds ?? (pitch ? [...PARAM_KINDS.slice(0, 1), 'note', 'hz', ...PARAM_KINDS.slice(1)] : PARAM_KINDS)).filter(
    (k) => depth < 2 || (k !== 'add' && k !== 'mul'),
  ) as ParamKind[];
  const options = [...new Set([kind, ...offered])].map((k) => ({ value: k, label: PARAM_KIND_LABELS[k] }));
  const convert = (k: ParamKind) => onChange(convertParam(value, k, { vars, duration, fallback: placeholder ?? 0 }));
  const modeSelect = (
    <div className="snd-param-mode" title="Value form">
      <SelectField value={kind} options={options} onChange={(k) => convert(k as ParamKind)} />
    </div>
  );

  if (kind === 'number') {
    return (
      <div className="snd-param">
        <div className="snd-row">
          <NumberField
            value={isNum(value) ? value : placeholder}
            step={step}
            min={min}
            max={max}
            onChange={onChange}
          />
          {pitch && isNum(value) && <span className="snd-chip mono" title="Note / frequency">{`${midiToNote(value)} · ${Math.round(mtof(value))} Hz`}</span>}
          {modeSelect}
        </div>
      </div>
    );
  }

  if (kind === 'note') {
    const midi = noteToMidi(value as string);
    return (
      <div className="snd-param">
        <div className="snd-row">
          <TextField value={value as string} mono placeholder="C#4" onChange={(t) => onChange(t.trim() || undefined)} />
          {midi !== null && <span className="snd-chip mono">{`MIDI ${midi} · ${Math.round(mtof(midi))} Hz`}</span>}
          {modeSelect}
        </div>
      </div>
    );
  }

  if (kind === 'hz') {
    const hz = (value as { hz: number }).hz;
    return (
      <div className="snd-param">
        <div className="snd-row">
          <NumberField label="Hz" value={hz} step={1} min={0} onChange={(v) => onChange({ hz: v })} />
          {modeSelect}
        </div>
      </div>
    );
  }

  if (kind === 'expr') {
    const expr = value as string;
    const result = tryEvalExpr(expr, vars);
    const missing = exprVariables(expr).filter((v) => !(v in vars));
    return (
      <div className="snd-param">
        <div className="snd-row">
          <TextField value={expr} mono placeholder="$pitch + 12" onChange={(t) => onChange(t.trim() === '' ? undefined : t)} />
          {modeSelect}
        </div>
        <span className={`snd-hint${result === null ? ' is-error' : ''}`}>
          {result !== null
            ? `= ${r4(result)}`
            : missing.length
              ? `unknown variable ${missing.map((m) => `$${m}`).join(', ')}`
              : 'invalid expression (+ - * /, ( ), min max clamp pow sin cos exp log abs mtof ftom db2lin lin2db)'}
        </span>
        {Object.keys(vars).length > 0 && (
          <div className="snd-chips">
            {Object.keys(vars).map((name) => (
              <button key={name} type="button" className="snd-chip is-button mono" onClick={() => onChange(`${expr} $${name}`.trim())}>
                ${name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (kind === 'env') {
    const obj = value as { env: EnvPoint[] };
    return (
      <div className="snd-param">
        <div className="snd-row">
          <span className="snd-hint">{`${obj.env.length} points`}</span>
          {modeSelect}
        </div>
        <EnvelopeEditor points={obj.env} min={min} max={max} duration={duration} step={step} onChange={(env) => onChange({ ...obj, env })} />
      </div>
    );
  }

  if (kind === 'adsr') {
    const obj = value as { adsr: unknown[]; peak?: unknown; base?: unknown };
    const adsr = [0, 1, 2, 3].map((i) => (isNum(obj.adsr[i]) ? (obj.adsr[i] as number) : [0.01, 0.1, 0.8, 0.2][i]!));
    const peak = isNum(obj.peak) ? obj.peak : 1;
    const base = isNum(obj.base) ? obj.base : 0;
    const setAdsr = (i: number, v: number) => {
      const next = [...adsr];
      next[i] = v;
      onChange({ ...obj, adsr: next });
    };
    const env = adsrToEnv(adsr, peak, base);
    const [lo, hi] = envRange(env, min, max);
    return (
      <div className="snd-param">
        <div className="snd-row">
          <NumberField label="A" title="Attack (s)" value={adsr[0]} step={0.005} min={0} onChange={(v) => setAdsr(0, v)} />
          <NumberField label="D" title="Decay (s)" value={adsr[1]} step={0.01} min={0} onChange={(v) => setAdsr(1, v)} />
          <NumberField label="S" title="Sustain level (0..1 of peak)" value={adsr[2]} step={0.05} min={0} max={1} onChange={(v) => setAdsr(2, v)} />
          <NumberField label="R" title="Release (s)" value={adsr[3]} step={0.01} min={0} onChange={(v) => setAdsr(3, v)} />
          {modeSelect}
        </div>
        <div className="snd-row">
          <NumberField label="Peak" value={peak} step={step} onChange={(v) => onChange({ ...obj, peak: v })} />
          <NumberField label="Base" value={base} step={step} onChange={(v) => onChange({ ...obj, base: v })} />
        </div>
        <Sparkline fn={(t) => evalEnv(env, t)} span={envDuration(env)} lo={lo} hi={hi} />
      </div>
    );
  }

  if (kind === 'lfo') {
    const obj = value as { lfo: LfoSpec };
    const l = obj.lfo;
    const set = (patch: Partial<LfoSpec>) => {
      const next: Record<string, unknown> = { ...l, ...patch };
      for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
      onChange({ ...obj, lfo: next });
    };
    const rate = isNum(l.rate) ? l.rate : 1;
    const depthV = isNum(l.depth) ? l.depth : 0;
    const center = isNum(l.center) ? l.center : 0;
    const span = Math.min(4, Math.max(0.25, 2 / Math.max(0.01, rate)));
    return (
      <div className="snd-param">
        <div className="snd-row">
          <SelectField value={l.shape ?? 'sine'} options={LFO_SHAPES} onChange={(s) => set({ shape: s as LfoShape })} />
          {modeSelect}
        </div>
        <div className="snd-row">
          <NumberField label="Rate" title="Hz" value={rate} step={0.1} min={0} onChange={(v) => set({ rate: v })} />
          <NumberField label="Depth" value={depthV} step={step} onChange={(v) => set({ depth: v })} />
          <NumberField label="Center" value={center} step={step} onChange={(v) => set({ center: v })} />
        </div>
        <div className="snd-row">
          <NumberField label="Phase" title="Start phase (cycles, 0..1)" value={isNum(l.phase) ? l.phase : 0} step={0.05} min={0} max={1} onChange={(v) => set({ phase: v })} />
          <SelectField
            value={l.sync}
            placeholder="Free rate"
            options={['', '1/1', '1/2', '1/4', '1/8', '1/16', '1/32', '1/4t', '1/8t', '1/16t', '1/4.', '1/8.'].map((s) => ({ value: s, label: s || 'Free rate' }))}
            onChange={(s) => set({ sync: s || undefined })}
          />
        </div>
        <Sparkline
          fn={(t) => lfoValue(l, t)}
          span={span}
          lo={center - (Math.abs(depthV) || 1)}
          hi={center + (Math.abs(depthV) || 1)}
        />
      </div>
    );
  }

  if (kind === 'random') {
    const obj = value as { random: unknown[] };
    const a = isNum(obj.random[0]) ? obj.random[0] : 0;
    const b = isNum(obj.random[1]) ? obj.random[1] : 1;
    return (
      <div className="snd-param">
        <div className="snd-row">
          <NumberField label="Min" value={a} step={step} min={min} max={max} onChange={(v) => onChange({ ...obj, random: [v, b] })} />
          <NumberField label="Max" value={b} step={step} min={min} max={max} onChange={(v) => onChange({ ...obj, random: [a, v] })} />
          {modeSelect}
        </div>
        <span className="snd-hint">a new value per render / note (seeded)</span>
      </div>
    );
  }

  if (kind === 'add' || kind === 'mul') {
    const obj = value as Record<string, unknown[]>;
    const parts = obj[kind]!;
    const setPart = (i: number, v: unknown) => {
      const next = [...parts];
      next[i] = v;
      onChange({ ...obj, [kind]: next });
    };
    return (
      <div className="snd-param">
        <div className="snd-row">
          <span className="snd-hint">{kind === 'add' ? 'sum of' : 'product of'}</span>
          <IconButton icon="plus" title="Add operand" size={13} onClick={() => onChange({ ...obj, [kind]: [...parts, kind === 'add' ? 0 : 1] })} />
          {modeSelect}
        </div>
        <div className="snd-param-nested">
          {parts.map((p, i) => (
            <div key={i} className="snd-param-operand">
              <ParamField value={p} onChange={(v) => setPart(i, v)} step={step} vars={vars} duration={duration} depth={depth + 1} />
              <IconButton
                icon="x"
                title="Remove operand"
                size={12}
                disabled={parts.length <= 1}
                onClick={() => onChange({ ...obj, [kind]: parts.filter((_, j) => j !== i) })}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="snd-param">
      <div className="snd-row">
        <span className="snd-hint">custom value</span>
        {modeSelect}
      </div>
      <JsonField value={value} onChange={onChange} rows={2} />
    </div>
  );
}
